// REACT
import React, { useState } from "react";

// MATERIAL-UI
import { List, Typography, Grid, Tooltip, useMediaQuery, Theme, useTheme } from "@mui/material";

// COMPONENTS
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";

// ASSESTS
import formatCoinName from "src/utils/formatCoinName";
import { GlobalSearchListProps } from "./types";
import { useHistory } from "react-router-dom";
import { trackEvent } from "src/Amplitude";
import WatchToggle from "../../WatchToggle";
import {
  GlobalSearchStyledListButton,
  GlobalSearchStyledListItem,
  GlobalSearchStyledListItemIcon,
  GlobalSearchStyledListItemRank,
  GlobalSearchStyledListItemText,
  GlobalSearchStyledSeeMoreStyle,
} from "../styles/GlobalSearchBar.styles";
import api from "src/store/features/api";
import { useUserSlice } from "src/store/features/user.slice";
import { useWatchlist } from "src/hooks/useWatchlist";
import { isTokenRestrictedForCountry } from "src/utils/restrictedCountry";

const SearchListGlobal = (props: GlobalSearchListProps) => {
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);
  const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up(1250));

  const userHoldings = useUserSlice().userHoldings;
  const { data: userData } = api.useGetUserDatav2Query();
  const userCountry = userData?.address?.country;
  const theme = useTheme();

  // Use watchlist hook for watchlist operations
  // The hook is available if needed, but WatchToggle handles its own state
  useWatchlist({
    trackingContext: "Global Search Bar",
  });

  const renderWatchList = (code: string, size?: string) => {
    // WatchToggle component handles its own state from Redux, so we don't need to pass watched prop
    // But we can still use the hook's isCoinInWatchlist for any conditional logic if needed
    return (
      <WatchToggle
        height={size ?? "18px"}
        width={size ?? "18px"}
        code={code}
        variant={"icon"}
        color={theme.palette.mode === "light" ? "#002035" : theme.palette.secondary.main}
      />
    );
  };

  const handleTradeBuyPage = (code: string) => {
    history.push(`/trade?code=${code.toLowerCase()}&type=buy`);
  };
  const handleTradeSellPage = (code: string) => {
    history.push(`/trade?code=${code.toLowerCase()}&type=sell`);
  };

  const handleDepositPage = (code: string) => {
    history.push(`/wallets/deposit?code=${code.toLowerCase()}`);
  };

  const handleCoinPage = (code: string) => {
    trackEvent(`Select Search Bar Navigate To Coins Page`, { token: code }, "Global Search Bar");
    history.push(`/coin/${code.toLowerCase()}`);
    props?.setOpenClose();
  };

  if (props.data.length === 0) {
    return <Typography>Could not be found.</Typography>;
  }

  // Limit the displayed items based on the 'expanded' state
  const limitedData = expanded ? props.data : props.data.slice(0, 7);
  const sortedtrending =
    limitedData && limitedData?.length > 0
      ? limitedData?.slice().sort((a, b) => a.rank - b.rank)
      : null;
  return (
    <div>
      <List>
        {sortedtrending?.map((el: any, i) => {
          const isRestricted = isTokenRestrictedForCountry(el, userCountry);
          const holdsToken = userHoldings?.some((item) => item.currencyConfig.code === el.code);
          const canBuy = el.permissions?.buy && !isRestricted;
          const canSellOrDeposit = el.permissions?.sell && (holdsToken || !isRestricted);
          const restrictedTooltip = isRestricted ? "This token is not available" : "";
          return (
          <GlobalSearchStyledListItem
            key={`${el.code}-${i}`}
            onClick={() => handleCoinPage(el.code)}>
            <Grid item lg={1} md={1} pb={"4px"} pr={"12px"}>
              <GlobalSearchStyledListItemRank>{el.rank} </GlobalSearchStyledListItemRank>
            </Grid>
            <Grid item lg={isLargeScreen ? 5.9 : 3.8} md={3.8}>
              <GlobalSearchStyledListItemText>
                <span style={{ display: "inline", paddingRight: "5px" }}>
                  <CountryOrCryptoFlag
                    name={el?.logoUrl || el.code}
                    size={isLargeScreen ? 20 : 15}
                  />
                </span>

                <GlobalSearchStyledListItemIcon>
                  {isLargeScreen && (
                    <Typography
                      style={{
                        display: "flex inline",
                        fontWeight: "bold",
                        paddingRight: "5px",
                      }}>
                      {formatCoinName(el.name)}
                    </Typography>
                  )}
                  {el.code}
                </GlobalSearchStyledListItemIcon>
              </GlobalSearchStyledListItemText>
            </Grid>
            <Grid
              item
              lg={isLargeScreen ? 4.5 : 8.5}
              md={8.5}
              pb={"4px"}
              style={{ display: "flex inline", placeContent: "center", padding: 0 }}>
              {/* Buy GlobalSearchStyledListButton */}
              {canBuy ? (
                <GlobalSearchStyledListButton
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTradeBuyPage(el.code);
                    trackEvent(`Select Search Bar Buy`, { token: el.code }, "Global Search Bar");
                  }}>
                  Buy
                </GlobalSearchStyledListButton>
              ) : (
                <Tooltip title={restrictedTooltip}>
                  <span onClick={(e) => e.stopPropagation()}>
                    <GlobalSearchStyledListButton variant="contained" disabled>
                      Buy
                    </GlobalSearchStyledListButton>
                  </span>
                </Tooltip>
              )}

              {/* Sell GlobalSearchStyledListButton */}
              {canSellOrDeposit ? (
                <GlobalSearchStyledListButton
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!holdsToken) {
                      handleDepositPage(el.code);
                      trackEvent(
                        `Select Search Bar Deposit`,
                        { token: el.code },
                        "Global Search Bar",
                      );
                    } else {
                      handleTradeSellPage(el.code);
                      trackEvent(`Select Search Bar Sell`, { token: el.code }, "Global Search Bar");
                    }
                  }}>
                  {holdsToken ? "Sell" : "Deposit"}
                </GlobalSearchStyledListButton>
              ) : (
                <Tooltip title={restrictedTooltip}>
                  <span onClick={(e) => e.stopPropagation()}>
                    <GlobalSearchStyledListButton variant="contained" disabled>
                      {holdsToken ? "Sell" : "Deposit"}
                    </GlobalSearchStyledListButton>
                  </span>
                </Tooltip>
              )}
            </Grid>
            <Grid
              item
              lg={0.5}
              md={0.5}
              onClick={(e) => {
                e.stopPropagation();
                trackEvent(
                  `Select Search Bar Add To Watchlist`,
                  { token: el.code },
                  "Global Search Bar",
                );
              }}
              style={{
                maxWidth: "26px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              {renderWatchList(el.code, "16px")}
            </Grid>
          </GlobalSearchStyledListItem>
          );
        })}
      </List>

      {props.data.length > 7 && (
        <GlobalSearchStyledSeeMoreStyle onClick={() => setExpanded(!expanded)}>
          {expanded ? "See Less" : `See More (${props.data.length - 7} more results)`}
        </GlobalSearchStyledSeeMoreStyle>
      )}
    </div>
  );
};

export default SearchListGlobal;
